import {
  FetchVisitsError,
  FetchVisitsParams,
  FetchVisitsResponse,
  Visit,
  VisitState,
  VisitsFiltersState,
  VisitsState,
} from './types';
import {
  cancelGroupVisit,
  cancelVisit,
  clearConfirmationCache,
  clearFilters,
  createVisit,
  fetchAllVisits,
  fetchPastVisits,
  fetchUpcomingVisits,
  fetchVisit,
  fetchVisitSeries,
  initializeVisitorRegistration,
  invalidateVisits,
  setCompanyFilter,
  setFiltersPinned,
  setHostFilter,
  setSearchFilter,
  setStatusFilter,
  updateVisit,
  updateVisitSeries,
} from './actions';

import { combineReducers } from 'redux';
import { createReducer } from 'typesafe-actions';
import { isStandardPaginationResult } from '../utils';
import { loadFiltersFromStorage, isFiltersPinned } from './util';

export const initialVisitState: VisitState = {
  loading: false,
  error: null,
  data: null,
};

export const emptyFiltersState: VisitsFiltersState = {
  search: '',
  status: [],
  companyUuids: [],
  companyOptions: [],
  hostUuids: [],
  hostOptions: [],
};

const loadedFilters = loadFiltersFromStorage();
export const initialFiltersState: VisitsFiltersState = loadedFilters ?? emptyFiltersState;

// Pinned state is derived from whether filters exist in localStorage
const initialFiltersPinned = isFiltersPinned();

export const initialState: VisitsState = {
  loading: false,
  mappedData: {},
  confirmationCache: {},
  visitorRegistrationInitialized: false,

  upcomingVisitsData: [],
  upcomingVisitsTotal: null,

  allVisitsData: [],
  allVisitsTotal: null,

  pastVisitsData: [],
  pastVisitsTotal: null,

  createVisitQueryState: {
    error: null,
    loading: false,
  },
  suppressVisitConfirmationRedirect: false,
  filters: initialFiltersState,
  filtersPinned: initialFiltersPinned,
  invalidationToken: 0,
  visitSeries: {
    loading: false,
    error: null,
    data: null,
  },
};

// A background refresh (the activity SSE stream re-reading a stale list) must not drive the shared
// spinner: turning `loading` on for it collapses the mobile list to skeletons and greys out an open
// delete dialog mid-action. Only user-initiated fetches flip it — and, symmetrically, a background
// completion must not clear the spinner out from under a user-initiated fetch that is still running.
const isBackgroundFetch = (payload: { background?: boolean }): boolean => payload.background === true;

const loading = createReducer(initialState.loading)
  .handleAction(fetchUpcomingVisits.request, (state, action) => (isBackgroundFetch(action.payload) ? state : true))
  .handleAction(fetchUpcomingVisits.failure, (state, action) =>
    isBackgroundFetch(action.payload.params) ? state : false,
  )
  .handleAction(fetchUpcomingVisits.success, (state, action) =>
    isBackgroundFetch(action.payload.params) ? state : false,
  )
  .handleAction(fetchPastVisits.request, (state, action) => (isBackgroundFetch(action.payload) ? state : true))
  .handleAction(fetchPastVisits.failure, (state, action) => (isBackgroundFetch(action.payload.params) ? state : false))
  .handleAction(fetchPastVisits.success, (state, action) => (isBackgroundFetch(action.payload.params) ? state : false))
  .handleAction(fetchAllVisits.request, (state, action) => (isBackgroundFetch(action.payload) ? state : true))
  .handleAction(fetchAllVisits.failure, (state, action) => (isBackgroundFetch(action.payload.params) ? state : false))
  .handleAction(fetchAllVisits.success, (state, action) => (isBackgroundFetch(action.payload.params) ? state : false));

const storeFetchedVisits = (
  state: Record<string, VisitState>,
  { payload }: { payload: FetchVisitsResponse },
): Record<string, VisitState> => {
  const visits: Visit[] = isStandardPaginationResult(payload.result) ? payload.result.data.visits : payload.result.data;
  return {
    ...state,
    ...visits.reduce(
      (result, visit) => ({
        ...result,
        [visit.id]: {
          ...initialVisitState,
          data: visit,
        },
      }),
      {} as Record<string, VisitState>,
    ),
  };
};

const mappedData = createReducer(initialState.mappedData)
  .handleAction(fetchUpcomingVisits.success, storeFetchedVisits)
  .handleAction(fetchPastVisits.success, storeFetchedVisits)
  .handleAction(fetchAllVisits.success, storeFetchedVisits)
  .handleAction(fetchVisit.request, (state, action) => ({
    ...state,
    [action.payload.visitId]: {
      ...initialVisitState,
      ...state[action.payload.visitId],
      loading: true,
    },
  }))
  .handleAction(fetchVisit.success, (state, action) => ({
    ...state,
    [action.payload.params.visitId]: {
      error: null,
      data: action.payload.result,
      loading: false,
    },
  }))
  .handleAction(fetchVisit.failure, (state, action) => ({
    ...state,
    [action.payload.params.visitId]: {
      error: action.payload.error,
      data: null,
      loading: false,
    },
  }))
  .handleAction(cancelVisit.request, (state, action) => ({
    ...state,
    [action.payload.visitId]: {
      ...state[action.payload.visitId],
      loading: true,
    },
  }))
  .handleAction(cancelVisit.success, (state, action) => {
    const newState = { ...state };
    delete newState[action.payload.visitId];

    return newState;
  })
  .handleAction(cancelVisit.failure, (state, action) => ({
    ...state,
    [action.payload.params.visitId]: {
      ...state[action.payload.params.visitId],
      loading: false,
      error: action.payload.error,
    },
  }))
  .handleAction(cancelGroupVisit.success, (state) => state)
  .handleAction(cancelGroupVisit.failure, (state) => state)
  .handleAction(createVisit.success, (state, action) => ({
    ...state,
    [action.payload.id]: {
      ...initialVisitState,
      data: action.payload,
    },
  }))
  .handleAction(updateVisit.request, (state, action) => ({
    ...state,
    [action.payload.params.visitId]: {
      ...state[action.payload.params.visitId],
      loading: true,
    },
  }))
  .handleAction(updateVisit.success, (state, action) => ({
    ...state,
    [action.payload.result.id]: {
      ...initialVisitState,
      data: action.payload.result,
      loading: false,
    },
  }))
  .handleAction(updateVisit.failure, (state, action) => ({
    ...state,
    [action.payload.params.visitId]: {
      ...state[action.payload.params.visitId],
      loading: false,
      error: action.payload.error,
    },
  }));

const confirmationCache = createReducer(initialState.confirmationCache)
  .handleAction(createVisit.success, (state, action) => ({
    ...state,
    [action.payload.id]: action.payload,
  }))
  .handleAction(updateVisit.success, (state, action) => {
    if (!action.payload.result.series_dates?.length) {
      return state;
    }
    return {
      ...state,
      [action.payload.result.id]: action.payload.result,
    };
  })
  .handleAction(clearConfirmationCache, (state, action) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [action.payload.visitId]: _removed, ...remainingCache } = state;
    return remainingCache;
  });

const visitorRegistrationInitialized = createReducer(initialState.visitorRegistrationInitialized).handleAction(
  initializeVisitorRegistration,
  () => true,
);

const isItemInCurrentRequest = (
  index: number,
  { offset, limit }: Pick<FetchVisitsParams, 'limit' | 'offset'>,
): boolean => index >= offset && index < limit + offset;

const handleFetchVisitsRequest = (state: VisitState[], { payload }: { payload: FetchVisitsParams }) => {
  const length = Math.max(state.length, payload.limit + payload.offset);

  return Array.from({ length }, (_, index: number) => {
    const oldVisitState = state[index];

    return {
      ...initialVisitState,
      ...oldVisitState,
      ...(isItemInCurrentRequest(index, payload) ? { loading: true } : {}),
    };
  });
};

const handleFetchVisitsSuccess = (state: VisitState[], { payload }: { payload: FetchVisitsResponse }) => {
  const data: Visit[] = isStandardPaginationResult(payload.result) ? payload.result.data.visits : payload.result.data;
  const requestedLimit = (payload.params as FetchVisitsParams).limit;
  const requestedOffset = (payload.params as FetchVisitsParams).offset;
  const isBackground = payload.params.background === true;

  // Detect if the API is honoring pagination parameters:
  // - If total > limit AND we received ≤ limit items, API is paginating correctly (e.g., 50 items from 351 total)
  // - If we received all items despite offset/limit, API ignored pagination (e.g., 351 items returned)
  const apiRespectsOffset = payload.result.total > requestedLimit && data.length <= requestedLimit;

  // Normalize the data to ensure we have the correct slice:
  // - If API paginated correctly, use the data as-is
  // - If API returned all data, manually slice to get our requested range
  const paginatedData = apiRespectsOffset ? data : data.slice(requestedOffset, requestedOffset + requestedLimit);

  // Initial load optimization: When fetching the first page (offset 0) with proper pagination,
  // create a fresh state array pre-sized to total count with only the first page populated.
  // This establishes the full length upfront and ensures hasMore calculations work correctly.
  //
  // A BACKGROUND offset-0 re-read (the activity SSE refresh) must NOT take this rebuild branch: it can
  // land after a concurrent loadMore under `mergeMap`, and rebuilding from scratch would discard every
  // page past the first that the user just scrolled in. The fall-through branch below sizes to the new
  // total and preserves `state[index]` outside the requested range, and the refresh re-reads the whole
  // loaded window (limit = max(loadedCount, batchSize)), so nothing goes stale by skipping the rebuild.
  if (requestedOffset === 0 && apiRespectsOffset && !isBackground) {
    const length = payload.result.total;

    return Array.from({ length }, (_, index: number) => {
      if (index < paginatedData.length) {
        return {
          loading: false,
          error: null,
          data: paginatedData[index],
        };
      }
      return initialVisitState;
    });
  }

  // Subsequent page loads or non-paginated responses:
  // Maintain a state array sized to the current total, filling in the requested range
  // while preserving previously loaded data at other indices.
  const length = payload.result.total;

  return Array.from({ length }, (_, index: number) => {
    // If this index falls within the newly fetched range, populate it with the corresponding visit
    if (index >= requestedOffset && index < requestedOffset + requestedLimit) {
      const localIndex = index - requestedOffset;
      const visit = paginatedData[localIndex];
      return visit
        ? {
            loading: false,
            error: null,
            data: visit,
          }
        : initialVisitState;
    }

    // Preserve existing state for indices outside the current request range
    return state[index] ?? initialVisitState;
  });
};

const handleFetchVisitsError = (state: VisitState[], { payload }: { payload: FetchVisitsError }) => {
  const length = Math.max(state.length, payload.params.limit + payload.params.offset);

  return Array.from({ length }, (_, index: number) => {
    const oldVisitState = state[index];

    return isItemInCurrentRequest(index, payload.params as FetchVisitsParams)
      ? {
          loading: false,
          data: null,
          error: payload.error,
        }
      : oldVisitState;
  });
};

const upcomingVisitsData = createReducer(initialState.upcomingVisitsData)
  .handleAction(fetchUpcomingVisits.request, (state, action) =>
    handleFetchVisitsRequest(state, { payload: action.payload as FetchVisitsParams }),
  )
  .handleAction(fetchUpcomingVisits.success, handleFetchVisitsSuccess)
  .handleAction(fetchUpcomingVisits.failure, handleFetchVisitsError)
  .handleAction(cancelVisit.success, () => [])
  .handleAction(cancelVisit.failure, () => [])
  .handleAction(cancelGroupVisit.success, () => [])
  .handleAction(cancelGroupVisit.failure, () => [])
  .handleAction(createVisit.success, () => [])
  .handleAction(updateVisit.success, () => []);

const upcomingVisitsTotal = createReducer(initialState.upcomingVisitsTotal)
  .handleAction(fetchUpcomingVisits.request, (state) => state)
  .handleAction(fetchUpcomingVisits.failure, (state) => state)
  .handleAction(fetchUpcomingVisits.success, (_state, action) => action.payload.result?.total ?? null)
  .handleAction(cancelVisit.success, () => null)
  .handleAction(cancelVisit.failure, () => null)
  .handleAction(cancelGroupVisit.success, () => null)
  .handleAction(cancelGroupVisit.failure, () => null)
  .handleAction(createVisit.success, () => null);

const pastVisitsData = createReducer(initialState.pastVisitsData)
  .handleAction(fetchPastVisits.request, (state, action) =>
    handleFetchVisitsRequest(state, { payload: action.payload as FetchVisitsParams }),
  )
  .handleAction(fetchPastVisits.success, handleFetchVisitsSuccess)
  .handleAction(fetchPastVisits.failure, handleFetchVisitsError)
  .handleAction(cancelVisit.success, () => [])
  .handleAction(cancelVisit.failure, () => [])
  .handleAction(cancelGroupVisit.success, () => [])
  .handleAction(cancelGroupVisit.failure, () => []);

const pastVisitsTotal = createReducer(initialState.pastVisitsTotal)
  .handleAction(fetchPastVisits.request, (state) => state)
  .handleAction(fetchPastVisits.failure, (state) => state)
  .handleAction(fetchPastVisits.success, (_state, action) => action.payload.result?.total ?? null)
  .handleAction(cancelVisit.success, () => null)
  .handleAction(cancelVisit.failure, () => null)
  .handleAction(cancelGroupVisit.success, () => null)
  .handleAction(cancelGroupVisit.failure, () => null);

const allVisitsData = createReducer(initialState.allVisitsData)
  .handleAction(fetchAllVisits.request, (state, action) =>
    handleFetchVisitsRequest(state, { payload: action.payload as FetchVisitsParams }),
  )
  .handleAction(fetchAllVisits.success, handleFetchVisitsSuccess)
  .handleAction(fetchAllVisits.failure, handleFetchVisitsError)
  .handleAction(cancelVisit.success, () => [])
  .handleAction(cancelVisit.failure, () => [])
  .handleAction(cancelGroupVisit.success, () => [])
  .handleAction(cancelGroupVisit.failure, () => []);

const allVisitsTotal = createReducer(initialState.allVisitsTotal)
  .handleAction(fetchAllVisits.request, (state) => state)
  .handleAction(fetchAllVisits.failure, (state) => state)
  .handleAction(fetchAllVisits.success, (_state, action) => action.payload.result?.total ?? null)
  .handleAction(cancelVisit.success, () => null)
  .handleAction(cancelVisit.failure, () => null)
  .handleAction(cancelGroupVisit.success, () => null)
  .handleAction(cancelGroupVisit.failure, () => null);

const createVisitQueryState = createReducer(initialState.createVisitQueryState)
  .handleAction([createVisit.request, updateVisitSeries.request], () => ({
    error: null,
    loading: true,
  }))
  .handleAction([createVisit.success, updateVisitSeries.success], () => ({
    error: null,
    loading: false,
  }))
  .handleAction(createVisit.failure, (_state, action) => ({
    error: action.payload.error,
    loading: false,
  }))
  .handleAction(updateVisitSeries.failure, (_state, action) => ({
    error: action.payload.error,
    loading: false,
  }));

const visitSeries = createReducer(initialState.visitSeries)
  .handleAction(fetchVisitSeries.request, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .handleAction(fetchVisitSeries.success, (_state, action) => ({
    loading: false,
    error: null,
    data: action.payload.result,
  }))
  .handleAction(fetchVisitSeries.failure, (_state, action) => ({
    loading: false,
    error: action.payload.error,
    data: null,
  }))
  .handleAction(updateVisitSeries.success, (_state, action) => ({
    loading: false,
    error: null,
    data: action.payload.result,
  }));

const suppressVisitConfirmationRedirect = createReducer(initialState.suppressVisitConfirmationRedirect)
  .handleAction(createVisit.request, (_state, action) => action.payload.suppressVisitConfirmationRedirect === true)
  .handleAction(createVisit.failure, () => false);

const filters = createReducer(initialState.filters)
  .handleAction(setSearchFilter, (state, action) => ({
    ...state,
    search: action.payload,
  }))
  .handleAction(setStatusFilter, (state, action) => ({
    ...state,
    status: action.payload,
  }))
  .handleAction(setCompanyFilter, (state, action) => ({
    ...state,
    companyUuids: action.payload.map(({ value }) => value as string),
    companyOptions: action.payload,
  }))
  .handleAction(setHostFilter, (state, action) => ({
    ...state,
    hostUuids: action.payload.map(({ value }) => value as string),
    hostOptions: action.payload,
  }))
  .handleAction(clearFilters, () => emptyFiltersState);

// filtersPinned state is derived from localStorage, but we still need to track it in Redux
// for UI updates. When setFiltersPinned is called, it means filters were saved/cleared
// so we update the state accordingly
const filtersPinned = createReducer(initialFiltersPinned).handleAction(
  setFiltersPinned,
  (_state, action) => action.payload,
);

const invalidationToken = createReducer(initialState.invalidationToken).handleAction(
  invalidateVisits,
  (state) => state + 1,
);

export default combineReducers({
  loading,
  mappedData,
  confirmationCache,
  visitorRegistrationInitialized,
  upcomingVisitsData,
  upcomingVisitsTotal,
  pastVisitsData,
  pastVisitsTotal,
  allVisitsData,
  allVisitsTotal,
  createVisitQueryState,
  suppressVisitConfirmationRedirect,
  filters,
  filtersPinned,
  invalidationToken,
  visitSeries,
});
